import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Collapse,
  Button,
  Progress,
  Table,
  Spinner,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  UncontrolledTooltip,
} from "reactstrap";
import {
  FaMoneyBillWave,
  FaChartPie,
  FaBalanceScale,
  FaChevronDown,
  FaChevronUp,
  FaChevronRight,
  FaBuilding,
  FaQrcode,
  FaMobileAlt,
  FaMoneyCheckAlt,
  FaCreditCard,
  FaBookOpen,
  FaFileInvoiceDollar,
  FaStethoscope,
  FaInfoCircle,
} from "react-icons/fa";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  getFinanceDashboardData,
} from "helpers/backend_helper";

// ─── Design Tokens ────────────────────────────────────────────────────────────
const TEXT_DARK = "var(--talc-text-primary)";
const TEXT_MUTED = "var(--talc-text-muted)";
const COLOR_SUCCESS = "var(--talc-success)";
const COLOR_DANGER = "var(--talc-danger)";
const COLOR_INDIGO = "var(--talc-info-text)";

// ─── Chart Color Palettes ────────────────────────────────────────────────────
const CHART_COLORS = [
  "var(--talc-info-text)", "var(--talc-success-text)", "var(--talc-warning-text)", "var(--talc-danger-text)", "var(--talc-neutral-text)",
  "var(--talc-info-text)", "var(--talc-warning-text)", "var(--talc-success-text)", "var(--talc-neutral-text)", "var(--talc-success-text)",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  `₹${Number(Math.round(Number(amount || 0))).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
  })}`;

const calculatePercentage = (paid, total) => {
  const p = Number(paid || 0);
  const t = Number(total || 0);
  return t === 0 ? 0 : ((p / t) * 100).toFixed(1);
};

const buildCalcLabel = (components) => {
  const parts = components
    .filter((c) => Number(c.value) !== 0)
    .map((c, i) => {
      const value = Number(c.value || 0);
      const sign = value < 0 ? " - " : i === 0 ? "" : " + ";
      return `${sign}${c.label} ${formatCurrency(Math.abs(value))}`;
    });
  return parts.length > 1 ? parts.join("") : null;
};

const buildCalcFormula = (components) => {
  const parts = components
    .filter((c) => Number(c.value) !== 0)
    .map((c, i) => {
      const value = Number(c.value || 0);
      const sign = value < 0 ? " - " : i === 0 ? "" : " + ";
      return `${sign}${c.label}`;
    });
  return parts.length > 1 ? parts.join("") : null;
};

const TooltipRows = ({ rows, total }) => (
  <div style={{ minWidth: "240px", textAlign: "left" }}>
    {rows.map((r, i) => (
      <div
        key={i}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "18px",
          padding: "2px 0",
        }}
      >
        <span style={{ opacity: 0.85 }}>{r.label}</span>
        <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{r.value}</span>
      </div>
    ))}
    {total && (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "18px",
          borderTop: "1px solid rgba(255,255,255,0.35)",
          marginTop: "4px",
          paddingTop: "5px",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        <span>{total.label}</span>
        <span>{total.value}</span>
      </div>
    )}
  </div>
);

const InfoLabel = ({ id, label, tooltip, labelStyle = {} }) => (
  <span className="d-inline-flex align-items-center gap-1">
    <style>{`.talc-info-tooltip.tooltip-inner { max-width: 380px !important; text-align: left; }`}</style>
    <span style={labelStyle}>{label}</span>
    <span
      id={id}
      style={{ cursor: "help", color: "var(--talc-text-muted)", display: "inline-flex", alignItems: "center" }}
    >
      <FaInfoCircle size={11} />
    </span>
    <UncontrolledTooltip target={id} placement="top" autohide={false} innerClassName="talc-info-tooltip">
      <div style={{ textAlign: "left", whiteSpace: "pre-line", fontSize: "0.78rem", lineHeight: 1.5 }}>
        {tooltip}
      </div>
    </UncontrolledTooltip>
  </span>
);

const LateFeeBreakdownCard = ({
  title,
  expected,
  paid,
  refunded,
  historicalRefund = 0,
  unpaid,
  historicalPaid = 0,
  tooltipKey = "",
  unpaidTooltip = "",
}) => {
  const isSettled = Number(unpaid || 0) <= 0;

  return (
    <div
      className="p-3 rounded-3"
      style={{
        background: "var(--talc-text-white)",
        border: "1px solid var(--talc-border)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-2.5 pb-2 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <span style={{ width: 26, height: 26, borderRadius: "6px", background: "var(--talc-warning-bg)", color: "var(--talc-warning-text)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <FaFileInvoiceDollar size={12} />
          </span>
          <span className="fw-bold" style={{ fontSize: "0.85rem", color: "var(--talc-text-primary)" }}>
            {title}
          </span>
        </div>
        <span
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            padding: "3px 8px",
            borderRadius: "12px",
            background: isSettled ? "var(--talc-success-bg)" : "var(--talc-danger-bg)",
            color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)",
            border: `1px solid ${isSettled ? "var(--talc-success-border)" : "var(--talc-danger-border)"}`,
          }}
        >
          {isSettled ? "✓ Fully Settled" : "Pending"}
        </span>
      </div>
      <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
        <div className="d-flex justify-content-between py-1 px-1">
          <span style={{ color: "var(--talc-text-secondary)" }}>Late Fee Expected</span>
          <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(expected)}</span>
        </div>
        <div className="d-flex justify-content-between py-1 px-1">
          <span style={{ color: "var(--talc-success-text)", fontWeight: 600 }}>Late Fee Paid</span>
          <span className="fw-bold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(paid)}</span>
        </div>
        <div className="d-flex justify-content-between py-1 px-1">
          <span style={{ color: "var(--talc-text-secondary)" }}>Previous Program Late Fee Paid</span>
          <span className="fw-bold" style={{ color: "var(--talc-warning-text)" }}>-{formatCurrency(historicalPaid)}</span>
        </div>
        <div className="d-flex justify-content-between py-1 px-1">
          <span style={{ color: "var(--talc-text-secondary)" }}>Previous Program Late Fee Refund</span>
          <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(historicalRefund)}</span>
        </div>
        <div className="d-flex justify-content-between py-1 px-1">
          <span style={{ color: "var(--talc-text-secondary)" }}>Late Fee Refund</span>
          <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(refunded)}</span>
        </div>
        <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "var(--talc-border)" }}>
          <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)" }}>
            {unpaidTooltip ? (
              <InfoLabel
                id={`late-unpaid-${tooltipKey}`}
                label="Late Fee Unpaid"
                tooltip={unpaidTooltip}
                labelStyle={{ color: "inherit", fontWeight: 700 }}
              />
            ) : (
              "Late Fee Unpaid"
            )}
          </span>
          <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)" }}>{formatCurrency(unpaid)}</span>
        </div>
      </div>
    </div>
  );
};

// ─── Center Palette Generator (3 clean, professional options) ────────────────
const CENTER_PALETTES = [
  { bg: "var(--talc-success-bg)", border: "var(--talc-success-border)", iconColor: "var(--talc-success-text)" },
  { bg: "var(--talc-info-bg)", border: "var(--talc-info-border)", iconColor: "var(--talc-info-text)" },
  { bg: "var(--talc-danger-bg)", border: "var(--talc-danger-border)", iconColor: "var(--talc-danger-text)" },
  { bg: "var(--talc-bg-subtle)", border: "var(--talc-border)", iconColor: "var(--talc-text-secondary)" },
  { bg: "var(--talc-success-bg)", border: "var(--talc-success-border)", iconColor: "var(--talc-success-text)" },
  { bg: "var(--talc-info-bg)", border: "var(--talc-info-border)", iconColor: "var(--talc-info-text)" },
];

// ─── Main Academic Fees Dashboard ─────────────────────────────────────────────
const AcademicFeesDashboard = ({ aYear }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [expandedCenter, setExpandedCenter] = useState(null);
  const [showAllCenters, setShowAllCenters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(aYear);
  const [activeDrawer, setActiveDrawer] = useState(null);

  useEffect(() => {
    setSelectedYear(aYear);
  }, [aYear]);

  useEffect(() => {
    if (!selectedYear) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const financeRes = await getFinanceDashboardData({ academic_year: selectedYear });

        if (financeRes?.data.success) {
          setDashboardData(financeRes.data.data);
          const firstCenter = financeRes.data.data.centers?.[0];
          if (firstCenter) setExpandedCenter(firstCenter.center_id);
        } else {
          console.error("Failed to fetch dashboard data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedYear]);

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center py-5" style={{ minHeight: 320 }}>
        <Spinner color="success" style={{ width: "2.5rem", height: "2.5rem" }} />
        <p className="text-muted mt-3 mb-0 fs-6">Loading fees dashboard data...</p>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center text-danger py-5 fw-semibold">
        Failed to load dashboard data. Please try again or refresh the page.
      </div>
    );
  }

  const { overall, centers = [] } = dashboardData;

  const historicalTermLateFeePaid = Number(overall.term_historical_latefee_paid_amount || 0);
  const currentTermLateFeePaid =
    overall.term_current_latefee_paid_amount !== undefined
      ? Number(overall.term_current_latefee_paid_amount || 0)
      : Math.max(0, Number(overall.term_latefee_paid_amount || 0) - historicalTermLateFeePaid);
  const totalTermLateFeePaid =
    Number(overall.term_latefee_paid_amount || 0) || currentTermLateFeePaid + historicalTermLateFeePaid;

  // ── Exact Business Logic Calculations ──────────────────────────────────────
  const expectedTermFee =
    Number(overall.term_total_amount || 0) + Number(overall.term_skipped_amount || 0);
  const termDisabled = Number(overall.term_disabled_amount || 0);
  const skippedTermFee = Number(overall.term_skipped_amount || 0);
  const termExpectedDiscount = Number(overall.term_expected_discount || 0);
  const termUsedDiscount = Number(overall.term_discounted_amount || 0);
  const termTotalDiscount = termExpectedDiscount + termUsedDiscount;

  const totalCollected =
    Number(overall.term_paid_amount) +
    Number(overall.term_historical_paid_amount) +
    Number(overall.otherfees_paid_amount) +
    Number(overall.observation_paid_amount) +
    Number(overall.registeration_paid_amount) +
    currentTermLateFeePaid +
    historicalTermLateFeePaid +
    Number(overall.otherfees_latefee_paid_amount);

  const totalAmount =
    expectedTermFee -
    termDisabled -
    skippedTermFee +
    Number(overall.term_expected_latefee_amount) +
    Number(overall.otherfees_total_amount) +
    Number(overall.observation_paid_amount) +
    Number(overall.registeration_paid_amount) +
    Number(overall.term_historical_paid_amount) +
    Number(overall.term_historical_latefee_paid_amount || 0) -
    termTotalDiscount;

  // Collected is already net of refunds in the backend totals, so the balance
  // is just the remaining obligation after current collected amounts.
  const totalBalance = Math.max(0, totalAmount - totalCollected);

  const excludedPercentageFees =
    Number(overall.observation_paid_amount || 0) + Number(overall.registeration_paid_amount || 0);
  const percentageCollected = totalCollected - excludedPercentageFees;
  const percentageTotal = totalAmount - excludedPercentageFees;

  const totalLateFee =
    totalTermLateFeePaid + Number(overall.otherfees_latefee_paid_amount);

  const totalPaymentChannelAmount = Object.values(overall.payment_channels || {}).reduce(
    (sum, val) => sum + Number(val?.net ?? val),
    0
  );

  const otherfeesExpectedLatefeeAmount =
    overall.otherfees_expected_latefee_amount !== undefined
      ? Number(overall.otherfees_expected_latefee_amount)
      : (centers || []).reduce((sum, c) => sum + Number(c.otherfees_expected_latefee_amount || 0), 0);

  // Late-fee outstanding is obligation-based: expected minus gross late fee
  // actually paid. Refunds reduce collected but must not re-open the balance.
  const termLateFeeBalance = Math.max(
    0,
    Number(overall.term_expected_latefee_amount || 0) -
      (Number(overall.term_latefee_paid_gross_amount || 0) -
        Number(overall.term_historical_latefee_paid_gross_amount || 0))
  );
  const otherLateFeeBalance = Math.max(
    0,
    Number(otherfeesExpectedLatefeeAmount) - Number(overall.otherfees_latefee_paid_gross_amount || 0)
  );
  const totalLateFeeBalance = termLateFeeBalance + otherLateFeeBalance;
  const termBaseBalance = Math.max(0, Number(overall.term_balance_amount || 0) - termLateFeeBalance);
  const otherBaseBalance = Math.max(0, Number(overall.otherfees_balance_amount || 0) - otherLateFeeBalance);

  const overallPercent = calculatePercentage(percentageCollected, percentageTotal);

  // ── Tooltip Breakdowns (Refined for maximum clarity) ───────────────────────
  const collectedTooltipRows = [
    {
      label: "Term Fees Paid",
      value: formatCurrency(
        Number(overall.term_paid_amount) + Number(overall.term_historical_paid_amount)
      ),
      dotColor: "var(--talc-success-text)",
    },
    {
      label: "Other Fees Paid",
      value: formatCurrency(overall.otherfees_paid_amount),
      dotColor: "var(--talc-success-text)",
    },
    {
      label: "Late Fees Paid",
      value: formatCurrency(
        currentTermLateFeePaid + Number(overall.otherfees_latefee_paid_amount)
      ),
      dotColor: "var(--talc-info-text)",
    },
    ...(historicalTermLateFeePaid > 0
      ? [
        {
          label: "Previous Program Late Fee Paid",
          value: formatCurrency(historicalTermLateFeePaid),
          dotColor: "var(--talc-text-muted)",
        },
      ]
      : []),
    {
      label: "Registration Fees",
      value: formatCurrency(overall.registeration_paid_amount),
      dotColor: "var(--talc-text-muted)",
    },
    {
      label: "Observation Fees",
      value: formatCurrency(overall.observation_paid_amount),
      dotColor: "var(--talc-text-muted)",
    },
  ];

  const billedTooltipRows = [
    {
      label: "Term Fees (Base Expected)",
      value: `+${formatCurrency(expectedTermFee)}`,
      dotColor: "var(--talc-info-text)",
      badge: "+ Addition",
      badgeColor: "var(--talc-info-bg)",
      badgeTextColor: "var(--talc-info-text)",
      subNote: "Base student tuition fees billed for the academic year",
    },
    ...(termDisabled > 0
      ? [
        {
          label: "Disabled Term Amount",
          value: `-${formatCurrency(termDisabled)}`,
          badge: "− Deduction",
          badgeColor: "var(--talc-danger-bg)",
          badgeTextColor: "var(--talc-danger-text)",
          dotColor: "var(--talc-danger-text)",
          subNote: "Term installments excluded from billing",
          isDeduction: true,
        },
      ]
      : []),
    ...(skippedTermFee > 0
      ? [
        {
          label: "Skipped Term Amount",
          value: `-${formatCurrency(skippedTermFee)}`,
          badge: "− Deduction",
          badgeColor: "var(--talc-danger-bg)",
          badgeTextColor: "var(--talc-danger-text)",
          dotColor: "var(--talc-danger-text)",
          subNote: "Skipped term installments excluded from billing",
          isDeduction: true,
        },
      ]
      : []),
    {
      label: "Other Fees (Activities & Transport)",
      value: `+${formatCurrency(
        Number(overall.otherfees_total_amount) - Number(otherfeesExpectedLatefeeAmount)
      )}`,
      badge: "+ Addition",
      badgeColor: "var(--talc-info-bg)",
      badgeTextColor: "var(--talc-info-text)",
      dotColor: "var(--talc-info-text)",
      subNote: "Non-tuition activity, materials and transport billed",
    },
    {
      label: "Late Fee Expected",
      value: `+${formatCurrency(
        Number(overall.term_expected_latefee_amount) + Number(otherfeesExpectedLatefeeAmount)
      )}`,
      badge: "+ Addition",
      badgeColor: "var(--talc-info-bg)",
      badgeTextColor: "var(--talc-info-text)",
      dotColor: "var(--talc-info-text)",
      subNote: "Fines & late payment charges accrued",
    },
    {
      label: "Previous Program Paid",
      value: `+${formatCurrency(overall.term_historical_paid_amount)}`,
      badge: "+ Addition",
      badgeColor: "var(--talc-bg-subtle)",
      badgeTextColor: "var(--talc-text-muted)",
      dotColor: "var(--talc-text-muted)",
      subNote: "Fees paid under the student's previous program, counted in this cycle",
    },
    ...(Number(overall.term_historical_latefee_paid_amount) > 0
      ? [
        {
          label: "Previous Program Late Fee Paid",
          value: `+${formatCurrency(overall.term_historical_latefee_paid_amount)}`,
          badge: "+ Addition",
          badgeColor: "var(--talc-bg-subtle)",
          badgeTextColor: "var(--talc-text-muted)",
          dotColor: "var(--talc-text-muted)",
          subNote: "Late fees paid under the student's previous program",
        },
      ]
      : []),
    {
      label: "Registration Fees",
      value: `+${formatCurrency(overall.registeration_paid_amount)}`,
      badge: "+ Addition",
      badgeColor: "var(--talc-bg-subtle)",
      badgeTextColor: "var(--talc-text-muted)",
      dotColor: "var(--talc-text-muted)",
      subNote: "One-time student onboarding charges",
    },
    {
      label: "Observation Fees",
      value: `+${formatCurrency(overall.observation_paid_amount)}`,
      badge: "+ Addition",
      badgeColor: "var(--talc-bg-subtle)",
      badgeTextColor: "var(--talc-text-muted)",
      dotColor: "var(--talc-text-muted)",
      subNote: "Direct admission & assessment charges",
    },
    {
      label: "Expected Discount & Concessions",
      value: `-${formatCurrency(termTotalDiscount)}`,
      badge: "− Deduction",
      badgeColor: "var(--talc-danger-bg)",
      badgeTextColor: "var(--talc-danger-text)",
      dotColor: "var(--talc-danger-text)",
      subNote: "Discounts and fee concessions subtracted from gross receivable",
      isDeduction: true,
    },
  ];

  const expectedLateFeeTotal =
    Number(overall.term_expected_latefee_amount) + Number(otherfeesExpectedLatefeeAmount);

  const balanceTooltipRows = [
    {
      label: "Term Fees Balance",
      value: formatCurrency(termBaseBalance),
      dotColor: "var(--talc-danger-text)",
    },
    {
      label: "Other Fees Balance",
      value: formatCurrency(otherBaseBalance),
      dotColor: "var(--talc-danger-text)",
    },
    ...(expectedLateFeeTotal > 0
      ? [
        {
          label: "Late Fee Expected",
          value: formatCurrency(expectedLateFeeTotal),
          dotColor: "var(--talc-warning-text)",
          badge: "+ Accrued",
          badgeColor: "var(--talc-warning-bg)",
          badgeTextColor: "var(--talc-warning-text)",
        },
      ]
      : []),
    ...(totalLateFee > 0
      ? [
        {
          label: "Late Fee Paid",
          value: formatCurrency(totalLateFee),
          dotColor: "var(--talc-success-text)",
        },
      ]
      : []),
    ...(totalLateFeeBalance > 0
      ? [
        {
          label: "Late Fee Unpaid",
          value: formatCurrency(totalLateFeeBalance),
          dotColor: "var(--talc-danger-text)",
        },
      ]
      : []),
  ];

  // Helper for Payment Channel Icon & Color
  const getChannelStyle = (channel, index) => {
    const ch = channel.toUpperCase();
    if (ch.includes("MIP")) {
      return {
        bg: "var(--talc-info-bg)",
        border: "var(--talc-info-border)",
        iconBg: "var(--talc-info-text)",
        icon: <FaQrcode color="var(--talc-text-white)" size={13} />,
      };
    }
    if (ch.includes("DIRECT") || ch.includes("SCREENSHOT")) {
      return {
        bg: "var(--talc-info-bg)",
        border: "var(--talc-info-border)",
        iconBg: "var(--talc-info-text)",
        icon: <FaMobileAlt color="var(--talc-text-white)" size={13} />,
      };
    }
    if (ch.includes("NDM") || ch.includes("MOM")) {
      return {
        bg: "var(--talc-success-bg)",
        border: "var(--talc-success-border)",
        iconBg: "var(--talc-success-text)",
        icon: <FaMoneyCheckAlt color="var(--talc-text-white)" size={13} />,
      };
    }
    const defaultStyles = [
      { bg: "var(--talc-info-bg)", border: "var(--talc-info-border)", iconBg: "var(--talc-info-text)", icon: <FaQrcode color="var(--talc-text-white)" size={13} /> },
      { bg: "var(--talc-info-bg)", border: "var(--talc-info-border)", iconBg: "var(--talc-info-text)", icon: <FaMobileAlt color="var(--talc-text-white)" size={13} /> },
      { bg: "var(--talc-success-bg)", border: "var(--talc-success-border)", iconBg: "var(--talc-success-text)", icon: <FaMoneyCheckAlt color="var(--talc-text-white)" size={13} /> },
      { bg: "var(--talc-bg-subtle)", border: "var(--talc-border)", iconBg: "var(--talc-text-muted)", icon: <FaCreditCard color="var(--talc-text-white)" size={13} /> },
    ];
    return defaultStyles[index % defaultStyles.length];
  };

  const displayedCenters = showAllCenters ? centers : centers.slice(0, 5);

  // Drawer Configuration Mapping
  const drawerConfigs = {
    collected: {
      title: "Total Paid Breakdown",
      subtitle: "Detailed breakdown of all received fee collections",
      totalLabel: "Total Paid",
      totalValue: formatCurrency(totalCollected),
      subText: `${overallPercent}% of total expected (${formatCurrency(totalAmount)})`,
      calcSubText: buildCalcLabel([
        { label: "term paid", value: Number(overall.term_paid_amount || 0) + Number(overall.term_historical_paid_amount || 0) },
        { label: "other paid", value: overall.otherfees_paid_amount },
        { label: "observation", value: overall.observation_paid_amount },
        { label: "registration", value: overall.registeration_paid_amount },
        { label: "late fee", value: currentTermLateFeePaid + Number(overall.otherfees_latefee_paid_amount || 0) },
        { label: "prev. program late", value: historicalTermLateFeePaid },
      ]),
      theme: "green",
      icon: <FaMoneyBillWave size={20} />,
      rows: collectedTooltipRows,
      accentColor: "var(--talc-success-text)",
      bgLight: "var(--talc-success-bg)",
      borderColor: "var(--talc-success-border)",
    },
    billed: {
      title: "Total Fee Breakdown",
      subtitle: "Detailed breakdown of all expected and invoiced fees",
      totalLabel: "Total Fee",
      totalValue: formatCurrency(totalAmount),
      subText: `Total expected amount across all categories and centers`,
      calcSubText: buildCalcLabel([
        { label: "term", value: overall.term_total_amount },
        { label: "late fee", value: Number(overall.term_expected_latefee_amount || 0) + Number(overall.otherfees_expected_latefee_amount || 0) },
        { label: "other", value: Number(overall.otherfees_total_amount || 0) - Number(overall.otherfees_expected_latefee_amount || 0) },
        { label: "observation", value: overall.observation_paid_amount },
        { label: "registration", value: overall.registeration_paid_amount },
        { label: "prev. program", value: overall.term_historical_paid_amount },
        { label: "prev. program late", value: overall.term_historical_latefee_paid_amount },
        { label: "disabled", value: -(overall.term_disabled_amount || 0) },
        { label: "discount", value: -(overall.term_expected_discount || 0) },
      ]),
      theme: "orange",
      icon: <FaChartPie size={20} />,
      rows: billedTooltipRows,
      accentColor: "var(--talc-warning-text)",
      bgLight: "var(--talc-warning-bg)",
      borderColor: "var(--talc-warning-border)",
    },
    balance: {
      title: "Balance Due Breakdown",
      subtitle: "Detailed breakdown of outstanding fee balances",
      totalLabel: "Total Balance Due",
      totalValue: formatCurrency(totalBalance),
      subText: `${(100 - Number(overallPercent)).toFixed(1)}% remaining balance to be collected`,
      calcSubText: buildCalcLabel([
        { label: "total fee", value: totalAmount },
        { label: "paid", value: -totalCollected },
      ]),
      theme: "red",
      icon: <FaBalanceScale size={20} />,
      rows: balanceTooltipRows,
      accentColor: "var(--talc-danger-text)",
      bgLight: "var(--talc-danger-bg)",
      borderColor: "var(--talc-danger-border)",
    },
  };

  const currentDrawer = activeDrawer ? drawerConfigs[activeDrawer] : null;

  return (
    <Container fluid className="px-0">

      {/* ── 1. Top KPI Summary Metrics ─────────────────────────────────────── */}
      <Row className="mb-0 g-2">
        {[
          {
            key: "billed",
            title: "Total Fee",
            amount: totalAmount,
            pill: "100%",
            theme: {
              bg: "var(--talc-info-bg)",
              border: "var(--talc-info-border)",
              title: "var(--talc-info-text)",
              pillBg: "var(--talc-info-bg)",
              pillText: "var(--talc-info-text)",
              pillBorder: "var(--talc-info-border)",
              buttonText: "var(--talc-info-text)",
              buttonBorder: "var(--talc-info-border)",
            },
          },
          {
            key: "collected",
            title: "Total Paid",
            amount: totalCollected,
            pill: `${overallPercent}%`,
            theme: {
              bg: "var(--talc-success-bg)",
              border: "var(--talc-success-border)",
              title: "var(--talc-success-text)",
              pillBg: "var(--talc-success-bg)",
              pillText: "var(--talc-success-text)",
              pillBorder: "var(--talc-success-border)",
              buttonText: "var(--talc-success-text)",
              buttonBorder: "var(--talc-success-border)",
            },
          },
          {
            key: "balance",
            title: "Balance Due",
            amount: totalBalance,
            pill: `${(100 - Number(overallPercent)).toFixed(1)}%`,
            theme: {
              bg: "var(--talc-danger-bg)",
              border: "var(--talc-danger-border)",
              title: "var(--talc-danger-text)",
              pillBg: "var(--talc-danger-bg)",
              pillText: "var(--talc-danger-text)",
              pillBorder: "var(--talc-danger-border)",
              buttonText: "var(--talc-danger-text)",
              buttonBorder: "var(--talc-danger-border)",
            },
          },
        ].map((item) => (
          <Col key={item.key} xs={12} md={6} lg={4}>
            <Card
              className="border-0"
              style={{
                borderRadius: "12px",
                border: `1px solid ${item.theme.border}`,
                boxShadow: "0 1px 4px rgba(15, 23, 42, 0.04)",
                background: item.theme.bg,
                overflow: "hidden",
                transition: "box-shadow 0.15s ease, transform 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(15, 23, 42, 0.08)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(15, 23, 42, 0.04)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <CardBody className="p-3">
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                  <div style={{ minWidth: 0, flex: "1 1 120px" }}>
                    <div style={{ fontSize: "0.84rem", fontWeight: 700, color: item.theme.title }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: "clamp(0.85rem, 2.2vw, 1.1rem)", fontWeight: 800, color: "var(--talc-text-primary)", marginTop: "6px", letterSpacing: "-0.3px", wordBreak: "break-word" }}>
                      {formatCurrency(item.amount)}
                    </div>
                    {item.subLabel && (
                      <div style={{ fontSize: "0.62rem", color: "var(--talc-text-muted)", marginTop: "3px", wordBreak: "break-word" }}>
                        {item.subLabel}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column align-items-start align-items-md-end" style={{ gap: "6px", flex: "0 0 auto" }}>
                    <span
                      style={{
                        minWidth: 80,
                        textAlign: "center",
                        padding: "3px 8px",
                        borderRadius: "20px",
                        background: item.theme.pillBg,
                        color: item.theme.pillText,
                        border: `1px solid ${item.theme.pillBorder}`,
                        fontSize: "0.66rem",
                        fontWeight: 700,
                      }}
                    >
                      {item.pill}
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveDrawer(item.key)}
                      className="d-inline-flex align-items-center gap-1.5"
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        border: `1px solid ${item.theme.buttonBorder}`,
                        background: "var(--talc-text-white)",
                        color: item.theme.buttonText,
                        fontSize: "0.66rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        transition: "all 0.2s ease",
                      }}
                    >
                      View breakdown <FaChevronRight size={8} />
                    </button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Side Offcanvas Breakdown Drawer ────────────────────────────────── */}
      <Offcanvas
        isOpen={Boolean(activeDrawer)}
        toggle={() => setActiveDrawer(null)}
        direction="end"
        style={{
          width: "440px",
          maxWidth: "92vw",
          boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.15)",
          border: "none",
          zIndex: 1060,
        }}
      >
        {currentDrawer && (
          <div className="d-flex flex-column h-100 bg-white">
            {/* Drawer Header */}
            <OffcanvasHeader
              toggle={() => setActiveDrawer(null)}
              className="border-bottom p-3"
            >
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "8px",
                    background: currentDrawer.bgLight,
                    border: `1px solid ${currentDrawer.borderColor}`,
                    color: currentDrawer.accentColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {currentDrawer.icon}
                </span>
                <div>
                  <h5 className="fw-bold mb-0" style={{ fontSize: "1rem", color: "var(--talc-text-primary)" }}>
                    {currentDrawer.title}
                  </h5>
                </div>
              </div>
            </OffcanvasHeader>

            <OffcanvasBody className="p-0 d-flex flex-column" style={{ overflowY: "auto" }}>
              {/* Quick Switch Tabs inside Drawer */}
              <div
                style={{
                  padding: "10px 16px",
                  backgroundColor: "var(--talc-bg-subtle)",
                  borderBottom: "1px solid var(--talc-border)",
                  display: "flex",
                  gap: "6px",
                }}
              >
                {[
                  { key: "billed", label: "Total Fee", color: "var(--talc-warning-text)" },
                  { key: "collected", label: "Paid", color: "var(--talc-success-text)" },
                  { key: "balance", label: "Balance Due", color: "var(--talc-danger-text)" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveDrawer(tab.key)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      borderRadius: "8px",
                      fontSize: "0.76rem",
                      fontWeight: activeDrawer === tab.key ? 700 : 500,
                      border: activeDrawer === tab.key ? `1px solid ${tab.color}` : "1px solid var(--talc-border)",
                      background: activeDrawer === tab.key ? "var(--talc-text-white)" : "transparent",
                      color: activeDrawer === tab.key ? tab.color : "var(--talc-text-muted)",
                      boxShadow: activeDrawer === tab.key ? "0 1px 3px rgba(0,0,0,0.05)" : "none",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Drawer Content */}
              <div style={{ padding: "18px 20px", flex: 1 }}>
                {/* Hero Metric Box */}
                <div
                  style={{
                    background: currentDrawer.bgLight,
                    border: `1px solid ${currentDrawer.borderColor}`,
                    borderRadius: "12px",
                    padding: "14px 16px",
                    marginBottom: "16px",
                  }}
                >
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", color: "var(--talc-text-muted)", letterSpacing: "0.5px" }}>
                    {currentDrawer.totalLabel}
                  </div>
                  <div style={{ fontSize: "1.55rem", fontWeight: 800, color: "var(--talc-text-primary)", marginTop: "2px", letterSpacing: "-0.5px" }}>
                    {currentDrawer.totalValue}
                  </div>
                  <div style={{ fontSize: "0.76rem", color: currentDrawer.accentColor, fontWeight: 600, marginTop: "4px" }}>
                    {currentDrawer.subText}
                  </div>
                  {currentDrawer.calcSubText && (
                    <div style={{ fontSize: "0.62rem", color: "var(--talc-text-muted)", marginTop: "2px", wordBreak: "break-word" }}>
                      {currentDrawer.calcSubText}
                    </div>
                  )}
                </div>

                {/* Itemized Breakdown List */}
                <div className="mb-2" style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--talc-text-secondary)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  Fee Components Breakdown
                </div>

                <div
                  style={{
                    background: "var(--talc-text-white)",
                    border: "1px solid var(--talc-border)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                  }}
                >
                  {currentDrawer.rows.map((row, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: "11px 14px",
                        borderBottom: idx === currentDrawer.rows.length - 1 ? "none" : "1px solid var(--talc-bg-subtle)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        transition: "background 0.15s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--talc-bg-subtle)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "var(--talc-text-white)")}
                    >
                      <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: row.dotColor || currentDrawer.accentColor,
                            flexShrink: 0,
                          }}
                        ></span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--talc-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.label}
                          </div>
                          {row.subNote && (
                            <div style={{ fontSize: "0.68rem", color: "var(--talc-text-muted)", marginTop: 2 }}>
                              {row.subNote}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="text-end ps-2 flex-shrink-0">
                        <div className="d-flex align-items-center justify-content-end gap-1.5">
                          {row.badge && (
                            <span
                              style={{
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                padding: "2px 5px",
                                borderRadius: "4px",
                                background: row.badgeColor || "var(--talc-bg-subtle)",
                                color: row.badgeTextColor || "var(--talc-text-secondary)",
                              }}
                            >
                              {row.badge}
                            </span>
                          )}
                          <span
                            style={{
                              fontSize: "0.84rem",
                              fontWeight: 700,
                              color: row.isDeduction ? "var(--talc-danger-text)" : "var(--talc-text-primary)",
                              fontFamily: "monospace, sans-serif",
                            }}
                          >
                            {row.value}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Detailed Fee Breakdowns (Balance Due only) */}
                {activeDrawer === "balance" && (
                  <div className="mt-4 d-flex flex-column gap-3">
                    <div className="mb-1" style={{ fontSize: "0.75rem", fontWeight: 700, color: "var(--talc-text-secondary)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                      Detailed Fee Breakdowns
                    </div>

                    {/* Term Fees Breakdown */}
                    {(() => {
                      const termBilled =
                        Number(overall.term_total_amount || 0) +
                        Number(overall.term_skipped_amount || 0) +
                        Number(overall.term_historical_paid_amount || 0);
                      const prevProgramDiscountUnpaid = Number(overall.term_historical_discount_amount || 0);
                      const prevProgramDiscountPaid = Number(overall.term_historical_discounted_amount || 0);
                      const prevProgramDiscountTotal = prevProgramDiscountUnpaid + prevProgramDiscountPaid;
                      const termExpectedDiscount = Number(overall.term_expected_discount || 0);
                      const termUsedDiscount = Number(overall.term_discounted_amount || 0);
                      const termDiscount = termExpectedDiscount + termUsedDiscount;
                      const termRefund = Number(overall.term_pure_refund_amount ?? overall.term_refund_amount ?? 0);
                      const termPaid = Number(overall.term_paid_amount || 0);
                      const termHistPaid = Number(overall.term_historical_paid_amount || 0);
                      const termLateTotal = Number(overall.term_expected_latefee_amount || 0);
                      const termLatePaid = Number(overall.term_latefee_paid_amount || 0);
                      const termLateRefund = Number(overall.term_late_refund_amount || 0);
                      const termLateUnpaid = Math.max(0, termLateTotal - termLatePaid - termLateRefund);
                      const hasTermLateFee = termLateTotal > 0 || termLatePaid > 0 || termLateRefund > 0;
                      const termBal = Number(overall.term_balance_amount || 0);
                      const isSettled = termBal <= 0;

                      return (
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "var(--talc-text-white)", border: "1px solid var(--talc-border)", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 28, height: 28, borderRadius: "8px", background: "var(--talc-info-bg)", color: "var(--talc-info-text)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                <FaBookOpen size={13} />
                              </span>
                              <span className="fw-bold" style={{ fontSize: "0.85rem", color: "var(--talc-text-primary)" }}>
                                Term Fees Breakdown
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: "12px",
                                background: isSettled ? "var(--talc-success-bg)" : "var(--talc-danger-bg)",
                                color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)",
                                border: isSettled ? "1px solid var(--talc-success-border)" : "1px solid var(--talc-danger-border)",
                              }}
                            >
                              {isSettled ? "✓ Fully Settled" : "Pending"}
                            </span>
                          </div>

                          <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Total Amount Billed</span>
                              <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(termBilled)}</span>
                            </div>

                            {Number(overall.term_skipped_amount) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-danger-text)" }}>Skipped Term Amount</span>
                                <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(overall.term_skipped_amount)}</span>
                              </div>
                            )}

                            {Number(overall.term_disabled_amount) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-danger-text)" }}>Disabled Term Amount</span>
                                <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(overall.term_disabled_amount)}</span>
                              </div>
                            )}

                            {termDiscount > 0 && (
                              <>
                                <div className="d-flex justify-content-between py-1 px-1">
                                  <span style={{ color: "var(--talc-text-secondary)" }}>Concession Discount (Total)</span>
                                  <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(termDiscount)}</span>
                                </div>
                                {termUsedDiscount > 0 && (
                                  <div className="d-flex justify-content-between py-1 px-1">
                                    <span style={{ color: "var(--talc-text-secondary)" }}>Discount Applied (Paid)</span>
                                    <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>-{formatCurrency(termUsedDiscount)}</span>
                                  </div>
                                )}
                              </>
                            )}

                            {termRefund > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-text-secondary)" }}>Fee Refund</span>
                                <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(termRefund)}</span>
                              </div>
                            )}

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-success-text)", fontWeight: 600 }}>Amount Paid</span>
                              <span className="fw-bold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(termPaid)}</span>
                            </div>

                            {prevProgramDiscountTotal > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-text-muted)" }}>Previous Program Discount</span>
                                <span className="fw-bold" style={{ color: "var(--talc-text-muted)" }}>{formatCurrency(prevProgramDiscountTotal)}</span>
                              </div>
                            )}

                            {prevProgramDiscountPaid > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-text-muted)" }}>Previous Program Discount Paid</span>
                                <span className="fw-bold" style={{ color: "var(--talc-text-muted)" }}>{formatCurrency(prevProgramDiscountPaid)}</span>
                              </div>
                            )}

                            {termHistPaid > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-info-text)" }}>Previous Program Paid</span>
                                <span className="fw-bold" style={{ color: "var(--talc-info-text)" }}>-{formatCurrency(termHistPaid)}</span>
                              </div>
                            )}

                            {hasTermLateFee && (
                              <div className="py-1 px-1 mt-0.5 d-flex flex-column gap-1">
                                <div className="d-flex justify-content-between">
                                  <span className="fw-bold" style={{ color: "var(--talc-warning-text)" }}>Total Late Fee</span>
                                  <span className="fw-bold" style={{ color: "var(--talc-warning-text)" }}>{formatCurrency(termLateTotal)}</span>
                                </div>
                                <div className="d-flex justify-content-between ps-2">
                                  <span style={{ color: "var(--talc-success-text)" }}>Late Fee Paid</span>
                                  <span style={{ color: "var(--talc-success-text)" }}>{formatCurrency(termLatePaid)}</span>
                                </div>
                                {termLateRefund > 0 && (
                                  <div className="d-flex justify-content-between ps-2">
                                    <span style={{ color: "var(--talc-text-secondary)" }}>Late Fee Refund</span>
                                    <span style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(termLateRefund)}</span>
                                  </div>
                                )}
                                <div className="d-flex justify-content-between ps-2">
                                  <span style={{ color: "var(--talc-text-secondary)" }}>Late Fee Unpaid</span>
                                  <span style={{ color: termLateUnpaid > 0 ? "var(--talc-danger-text)" : "var(--talc-text-muted)" }}>{formatCurrency(termLateUnpaid)}</span>
                                </div>
                              </div>
                            )}

                            <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "var(--talc-border)" }}>
                              <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)" }}>Remaining Balance</span>
                              <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)", fontSize: "0.85rem" }}>{formatCurrency(termBal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Other Fees Breakdown */}
                    {(() => {
                      const otherBilled = Number(overall.otherfees_total_amount || 0);
                      const otherRefund = Number(overall.otherfees_pure_refund_amount ?? overall.otherfees_refund_amount ?? 0);
                      const otherPaid = Number(overall.otherfees_paid_amount || 0);
                      const otherLateTotal = Number(overall.otherfees_expected_latefee_amount || otherfeesExpectedLatefeeAmount);
                      const otherLatePaid = Number(overall.otherfees_latefee_paid_amount || 0);
                      const otherLateRefund = Number(overall.otherfees_late_refund_amount || 0);
                      const otherLateUnpaid = Math.max(0, otherLateTotal - otherLatePaid - otherLateRefund);
                      const hasOtherLateFee = otherLateTotal > 0 || otherLatePaid > 0 || otherLateRefund > 0;
                      const otherBal = Number(overall.otherfees_balance_amount || 0);
                      const isSettled = otherBal <= 0;

                      return (
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "var(--talc-text-white)", border: "1px solid var(--talc-border)", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 28, height: 28, borderRadius: "8px", background: "var(--talc-warning-bg)", color: "var(--talc-warning-text)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                <FaFileInvoiceDollar size={13} />
                              </span>
                              <span className="fw-bold" style={{ fontSize: "0.85rem", color: "var(--talc-text-primary)" }}>
                                Other Fees Breakdown
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: "12px",
                                background: isSettled ? "var(--talc-success-bg)" : "var(--talc-danger-bg)",
                                color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)",
                                border: isSettled ? "1px solid var(--talc-success-border)" : "1px solid var(--talc-danger-border)",
                              }}
                            >
                              {isSettled ? "✓ Fully Settled" : "Pending"}
                            </span>
                          </div>

                          <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Total Amount Billed</span>
                              <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(otherBilled)}</span>
                            </div>

                            {otherRefund > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-text-secondary)" }}>Fee Refund</span>
                                <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(otherRefund)}</span>
                              </div>
                            )}

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-success-text)", fontWeight: 600 }}>Amount Paid</span>
                              <span className="fw-bold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(otherPaid)}</span>
                            </div>

                            {hasOtherLateFee && (
                              <div className="py-1 px-1 mt-0.5 d-flex flex-column gap-1">
                                <div className="d-flex justify-content-between">
                                  <span className="fw-bold" style={{ color: "var(--talc-warning-text)" }}>Total Late Fee</span>
                                  <span className="fw-bold" style={{ color: "var(--talc-warning-text)" }}>{formatCurrency(otherLateTotal)}</span>
                                </div>
                                <div className="d-flex justify-content-between ps-2">
                                  <span style={{ color: "var(--talc-success-text)" }}>Late Fee Paid</span>
                                  <span style={{ color: "var(--talc-success-text)" }}>{formatCurrency(otherLatePaid)}</span>
                                </div>
                                {otherLateRefund > 0 && (
                                  <div className="d-flex justify-content-between ps-2">
                                    <span style={{ color: "var(--talc-text-secondary)" }}>Late Fee Refund</span>
                                    <span style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(otherLateRefund)}</span>
                                  </div>
                                )}
                                <div className="d-flex justify-content-between ps-2">
                                  <span style={{ color: "var(--talc-text-secondary)" }}>Late Fee Unpaid</span>
                                  <span style={{ color: otherLateUnpaid > 0 ? "var(--talc-danger-text)" : "var(--talc-text-muted)" }}>{formatCurrency(otherLateUnpaid)}</span>
                                </div>
                              </div>
                            )}

                            <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "var(--talc-border)" }}>
                              <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)" }}>Remaining Balance</span>
                              <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)", fontSize: "0.85rem" }}>{formatCurrency(otherBal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Total Summary Footer Box */}
                <div
                  style={{
                    marginTop: "14px",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    background: currentDrawer.bgLight,
                    border: `1px solid ${currentDrawer.borderColor}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", color: currentDrawer.accentColor, letterSpacing: "0.4px" }}>
                    {currentDrawer.totalLabel}
                  </span>
                  <span style={{ fontSize: "0.98rem", fontWeight: 800, color: currentDrawer.accentColor, fontFamily: "monospace, sans-serif" }}>
                    {currentDrawer.totalValue}
                  </span>
                </div>
              </div>

              {/* Drawer Footer */}
              <div
                style={{
                  padding: "14px 20px",
                  borderTop: "1px solid var(--talc-border)",
                  backgroundColor: "var(--talc-text-white)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setActiveDrawer(null)}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid var(--talc-border)",
                    background: "var(--talc-bg-subtle)",
                    color: "var(--talc-text-secondary)",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--talc-border)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "var(--talc-bg-subtle)";
                  }}
                >
                  Close Breakdown Panel
                </button>
              </div>
            </OffcanvasBody>
          </div>
        )}
      </Offcanvas>

      {/* ── 2. Overall Payment Channels ────────────────────────────────────── */}
      {(() => {
        const channelEntries = Object.entries(overall.payment_channels || {})
          .sort(([, a], [, b]) => (Number(b?.received ?? b) || 0) - (Number(a?.received ?? a) || 0));
        const grossReceived = channelEntries.reduce((sum, [, chData]) => sum + Number(chData?.received ?? chData) || 0, 0);
        const totalRefunded = channelEntries.reduce((sum, [, chData]) => sum + (Number(chData?.refunded) || 0), 0);
        const netCollection = grossReceived - totalRefunded;
        return (
          <Card className="talc-card mb-3" style={{ borderRadius: "var(--talc-radius-lg)", background: "var(--talc-bg-card)" }}>
            <CardBody className="py-2.5 px-3.5">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="fw-bold" style={{ fontSize: "0.82rem", color: TEXT_DARK }}>
                  Overall Payment Channels
                </span>
                <span style={{ fontSize: "0.75rem", color: TEXT_MUTED }}>
                  Gross Received — <strong style={{ color: TEXT_DARK }}>{formatCurrency(grossReceived)}</strong>
                </span>
              </div>
              {channelEntries.length === 0 && (
                <div className="text-center py-2 text-muted" style={{ fontSize: "0.76rem" }}>
                  No payment channel records available.
                </div>
              )}
              <div className="d-flex flex-wrap gap-2">
                {channelEntries.map(([channel, chData], i) => {
                  const style = getChannelStyle(channel, i);
                  const received = Number(chData?.received ?? chData) || 0;
                  const refunded = Number(chData?.refunded) || 0;
                  const net = Number(chData?.net ?? received - refunded) || 0;
                  return (
                    <div
                      key={i}
                      className="flex-fill"
                      style={{
                        minWidth: "180px",
                        background: style.bg,
                        border: `1px solid ${style.border}`,
                        borderRadius: "10px",
                        padding: "7px 10px",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <div
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: "8px",
                            background: style.iconBg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {style.icon}
                        </div>
                        <div style={{ overflow: "hidden" }}>
                          <div
                            className="text-uppercase fw-semibold"
                            style={{
                              fontSize: "0.62rem",
                              color: "var(--talc-text-primary)",
                              letterSpacing: "0.3px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={channel}
                          >
                            {channel}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex flex-column" style={{ fontSize: "0.68rem", gap: "2px" }}>
                        <div className="d-flex justify-content-between">
                          <span style={{ color: "var(--talc-text-secondary)" }}>Received</span>
                          <span className="fw-semibold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(received)}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span style={{ color: "var(--talc-text-secondary)" }}>Refunded</span>
                          <span className="fw-semibold" style={{ color: refunded > 0 ? "var(--talc-danger-text)" : "var(--talc-text-muted)" }}>{formatCurrency(refunded)}</span>
                        </div>
                        <div className="d-flex justify-content-between pt-1" style={{ borderTop: `1px dashed ${style.border}` }}>
                          <span className="fw-bold" style={{ color: TEXT_DARK }}>Net</span>
                          <span className="fw-bold" style={{ color: TEXT_DARK }}>{formatCurrency(net)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {channelEntries.length > 0 && (
                <div className="d-flex justify-content-end gap-4 mt-2 pt-2 border-top" style={{ fontSize: "0.72rem" }}>
                  <span style={{ color: "var(--talc-text-secondary)" }}>
                    Total Refunded — <strong style={{ color: "var(--talc-danger-text)" }}>{formatCurrency(totalRefunded)}</strong>
                  </span>
                  <span style={{ color: "var(--talc-text-secondary)" }}>
                    Net Collection — <strong style={{ color: TEXT_DARK }}>{formatCurrency(netCollection)}</strong>
                  </span>
                </div>
              )}
            </CardBody>
          </Card>
        );
      })()}

      <div className="d-flex align-items-center justify-content-between mb-3 px-1">
        <h5 className="fw-bold mb-0" style={{ fontSize: "0.98rem", color: TEXT_DARK }}>
          Center-wise Fee Collection
        </h5>
        <span style={{ fontSize: "0.8rem", color: TEXT_MUTED }}>
          {centers.length} {centers.length === 1 ? "Center" : "Centers"} Total
        </span>
      </div>

      {/* ── 4. Center Cards List ───────────────────────────────────────────── */}
      {displayedCenters.map((center, cIndex) => {
        const centerHistoricalLateFeePaid = Number(center.term_historical_latefee_paid_gross_amount || 0);
        const centerHistoricalLateFeeNet = Number(center.term_historical_latefee_paid_amount || 0);
        const centerHistoricalLateFeeRefund = Number(center.term_historical_late_refund_amount || 0);
        const centerCurrentLateFeeRefund =
          center.term_current_late_refund_amount !== undefined
            ? Number(center.term_current_late_refund_amount || 0)
            : Math.max(
              0,
              Number(center.term_late_refund_amount || 0) - centerHistoricalLateFeeRefund
            );
        const centerCurrentLateFeePaid =
          center.term_current_latefee_paid_amount !== undefined
            ? Number(center.term_current_latefee_paid_amount || 0)
            : Math.max(0, Number(center.term_latefee_paid_amount || 0) - centerHistoricalLateFeeNet);
        const centerCurrentLateFeePaidGross = Math.max(
          0,
          Number(center.term_latefee_paid_gross_amount || 0) -
            Number(center.term_historical_latefee_paid_gross_amount || 0)
        );
        const centerOtherLateFeePaidGross = Number(center.otherfees_latefee_paid_gross_amount || 0);
        const centerHistoricalOtherLateFeePaid = Number(center.otherfees_historical_latefee_paid_gross_amount || 0);
        const centerHistoricalOtherLateFeeRefund = Number(center.otherfees_historical_late_refund_amount || 0);
        const centerCurrentOtherLateFeeRefund =
          center.otherfees_current_late_refund_amount !== undefined
            ? Number(center.otherfees_current_late_refund_amount || 0)
            : Math.max(
              0,
              Number(center.otherfees_late_refund_amount || 0) - centerHistoricalOtherLateFeeRefund
            );
        const centerOtherLateFeeUnpaid = Math.max(
          0,
          Number(center.otherfees_expected_latefee_amount || 0) - centerOtherLateFeePaidGross
        );

        // term_paid_amount is already net of all term refunds (incl. historical),
        // and term_latefee_paid_amount already includes prev-program late fees —
        // so only the previous-program term paid needs to be added here.
        const collected =
          Number(center.term_paid_amount) +
          Number(center.otherfees_paid_amount) +
          Number(center.term_latefee_paid_amount || 0) +
          Number(center.otherfees_latefee_paid_amount || 0) +
          Number(center.term_historical_paid_amount || 0);

        const totalAmt =
          Number(center.term_total_amount || 0) -
          Number(center.term_disabled_amount || 0) +
          Number(center.term_expected_latefee_amount || 0) +
          Number(center.otherfees_total_amount || 0) +
          Number(center.term_historical_paid_amount || 0) -
          Number(center.term_historical_refund_amount || 0) -
          (Number(center.term_expected_discount || 0) + Number(center.term_discounted_amount || 0)) -
          Number(center.term_refund_amount || 0) -
          Number(center.otherfees_refund_amount || 0);

        const collectionPercent = calculatePercentage(collected, totalAmt);
        const isExpanded = expandedCenter === center.center_id;

        const totalChannelReceived = Object.values(center.payment_channels || {}).reduce(
          (sum, val) => sum + Number(val?.received ?? val),
          0
        );
        const totalChannelRefund = Object.values(center.payment_channels || {}).reduce(
          (sum, val) => sum + Number(val?.refunded ?? 0),
          0
        );
        const totalChannel = totalChannelReceived - totalChannelRefund;

        const centerExpectedDiscount = Number(center.term_expected_discount || 0);
        const centerUsedDiscount = Number(center.term_discounted_amount || 0);
        const centerTotalDiscount = centerExpectedDiscount + centerUsedDiscount;

        const totalExpectedTooltip = (
          <TooltipRows
            rows={[
              { label: "Term Fee Total", value: `+${formatCurrency(center.term_total_amount || 0)}` },
              { label: "Disabled Term Amount", value: `-${formatCurrency(center.term_disabled_amount || 0)}` },
              { label: "Term Late Fee Expected", value: `+${formatCurrency(center.term_expected_latefee_amount || 0)}` },
              { label: "Other Fee Expected", value: `+${formatCurrency(center.otherfees_total_amount || 0)}` },
              { label: "Previous Program Paid", value: `+${formatCurrency(center.term_historical_paid_amount || 0)}` },
              { label: "Discount", value: `-${formatCurrency(centerTotalDiscount)}` },
              { label: "Term Refund", value: `-${formatCurrency(center.term_refund_amount || 0)}` },
              { label: "Other Refund", value: `-${formatCurrency(center.otherfees_refund_amount || 0)}` },
              { label: "Previous Program Refund", value: `-${formatCurrency(center.term_historical_refund_amount || 0)}` },
            ]}
            total={{ label: "Total Expected", value: formatCurrency(totalAmt) }}
          />
        );
        const termOtherPaidTooltip = (
          <TooltipRows
            rows={[
              { label: "Term Paid", value: `+${formatCurrency(center.term_paid_amount || 0)}` },
              { label: "Other Paid", value: `+${formatCurrency(center.otherfees_paid_amount || 0)}` },
            ]}
            total={{
              label: "Term + Other Paid",
              value: formatCurrency(Number(center.term_paid_amount || 0) + Number(center.otherfees_paid_amount || 0)),
            }}
          />
        );
        const overallPaidTooltip = (
          <TooltipRows
            rows={[
              { label: "Term Paid", value: `+${formatCurrency(center.term_paid_amount || 0)}` },
              { label: "Other Paid", value: `+${formatCurrency(center.otherfees_paid_amount || 0)}` },
              { label: "Term Late Fee Paid", value: `+${formatCurrency(centerCurrentLateFeePaid)}` },
              { label: "Other Late Fee Paid", value: `+${formatCurrency(center.otherfees_latefee_paid_amount || 0)}` },
              { label: "Previous Program Term Paid", value: `+${formatCurrency(center.term_historical_paid_amount || 0)}` },
              { label: "Previous Program Late Fee Paid", value: `+${formatCurrency(center.term_historical_latefee_paid_amount || 0)}` },
            ]}
            total={{ label: "Overall Paid", value: formatCurrency(collected) }}
          />
        );
        const termLateUnpaidTooltip = (
          <TooltipRows
            rows={[
              {
                label: "Late Fee Expected (incl. Prev Program)",
                value: `+${formatCurrency(Number(center.term_expected_latefee_amount || 0) + centerHistoricalLateFeePaid - centerHistoricalLateFeeRefund)}`,
              },
              { label: "Previous Program Late Fee Paid", value: `-${formatCurrency(centerHistoricalLateFeePaid)}` },
              { label: "Previous Program Late Fee Refund", value: `+${formatCurrency(centerHistoricalLateFeeRefund)}` },
              { label: "Current Late Paid (Gross)", value: `-${formatCurrency(centerCurrentLateFeePaidGross)}` },
            ]}
            total={{
              label: "Term Late Fee Unpaid",
              value: formatCurrency(Math.max(0, Number(center.term_expected_latefee_amount || 0) - centerCurrentLateFeePaidGross)),
            }}
          />
        );
        const otherLateUnpaidTooltip = (
          <TooltipRows
            rows={[
              { label: "Other Late Fee Expected", value: `+${formatCurrency(center.otherfees_expected_latefee_amount || 0)}` },
              { label: "Other Late Paid (Gross)", value: `-${formatCurrency(centerOtherLateFeePaidGross)}` },
            ]}
            total={{ label: "Other Late Fee Unpaid", value: formatCurrency(centerOtherLateFeeUnpaid) }}
          />
        );

        const totalExpectedSubLabel = buildCalcFormula([
          { label: "term", value: Number(center.term_total_amount || 0) + Number(center.term_skipped_amount || 0) },
          { label: "skipped", value: -(center.term_skipped_amount || 0) },
          { label: "disabled", value: -(center.term_disabled_amount || 0) },
          { label: "discount", value: -((center.term_expected_discount || 0) + (center.term_discounted_amount || 0)) },
          { label: "late fee", value: center.term_expected_latefee_amount },
          { label: "other", value: center.otherfees_total_amount },
          { label: "refunds", value: -((Number(center.term_refund_amount || 0) + Number(center.otherfees_refund_amount || 0))) },
        ]);

        const termOtherPaidSubLabel = buildCalcLabel([
          { label: "term paid", value: center.term_paid_amount },
          { label: "other paid", value: center.otherfees_paid_amount },
        ]);

        const lateFeePaidSubLabel = buildCalcLabel([
          { label: "term late", value: centerCurrentLateFeePaid },
          { label: "prev. program late", value: centerHistoricalLateFeePaid },
          { label: "other late", value: center.otherfees_latefee_paid_amount },
        ]);

        const overallPaidSubLabel = buildCalcLabel([
          { label: "term paid", value: center.term_paid_amount },
          { label: "other paid", value: center.otherfees_paid_amount },
          { label: "observation", value: center.observation_paid_amount },
          { label: "registration", value: center.registeration_paid_amount },
          { label: "late fee", value: Number(center.term_latefee_paid_amount || 0) + Number(center.otherfees_latefee_paid_amount || 0) },
          { label: "prev. program", value: Number(center.term_historical_paid_amount || 0) },
        ]);

        const palette = CENTER_PALETTES[cIndex % CENTER_PALETTES.length];

        return (
          <Card
            key={center.center_id}
            className="talc-card mb-2"
            style={{
              borderRadius: "var(--talc-radius-lg)",
              border: "1px solid var(--talc-border)",
              overflow: "hidden",
              background: "var(--talc-text-white)",
              transition: "box-shadow 0.2s ease",
            }}
          >
            {/* Center Summary Banner */}
            <div
              className="p-3 bg-white"
              style={{ cursor: "pointer" }}
              onClick={() => setExpandedCenter(isExpanded ? null : center.center_id)}
            >
              <Row className="align-items-center g-4">
                {/* Center Title & Location */}
                <Col xs={12} sm={4} md={3} className="d-flex align-items-center gap-2">
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      background: palette.bg,
                      border: `1px solid ${palette.border}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <FaBuilding color={palette.iconColor} size={16} />
                  </div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div
                      className="fw-bold text-uppercase"
                      style={{ fontSize: "0.92rem", color: TEXT_DARK, letterSpacing: "0.3px" }}
                    >
                      {center.center_name || "Unknown Center"}
                    </div>
                    <span
                      className="badge rounded-pill px-2 py-1"
                      style={{
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        background: center.center_status === 1 || center.center_status === "1"
                          ? "var(--talc-success-bg)"
                          : "var(--talc-danger-bg)",
                        color: center.center_status === 1 || center.center_status === "1"
                          ? "var(--talc-success-text)"
                          : "var(--talc-danger-text)",
                        border: `1px solid ${center.center_status === 1 || center.center_status === "1"
                          ? "var(--talc-success-border)"
                          : "var(--talc-danger-border)"}`,
                      }}
                    >
                      {center.center_status === 1 || center.center_status === "1" ? "Active" : "Inactive"}
                    </span>
                  </div>
                </Col>

                {/* Key Amounts Summary */}
                <Col xs={12} sm={5} md={6}>
                  <div className="d-flex flex-wrap align-items-center" style={{ gap: "12px 20px" }}>
                    <div style={{ minWidth: "90px", flex: "1 1 auto" }}>
                      <div style={{ fontSize: "0.7rem", color: TEXT_MUTED }}>
                        <InfoLabel
                          id={`center-expected-${center.center_id}`}
                          label="Total Fee"
                          tooltip={totalExpectedTooltip}
                          labelStyle={{ color: TEXT_MUTED, fontSize: "0.7rem" }}
                        />
                      </div>
                      <div className="fw-bold" style={{ fontSize: "0.92rem", color: "var(--talc-text-primary)", letterSpacing: "-0.2px", wordBreak: "break-word" }}>
                        {formatCurrency(totalAmt)}
                      </div>
                      {totalExpectedSubLabel && (
                        <div style={{ fontSize: "0.62rem", color: "var(--talc-text-muted)", marginTop: "2px", wordBreak: "break-word", lineHeight: 1.4 }}>
                          {totalExpectedSubLabel}
                        </div>
                      )}
                    </div>

                    <div style={{ minWidth: "90px", flex: "1 1 auto" }}>
                      <div style={{ fontSize: "0.7rem", color: TEXT_MUTED }}>
                        <InfoLabel
                          id={`center-paid-${center.center_id}`}
                          label="Term + Other Paid"
                          tooltip={termOtherPaidTooltip}
                          labelStyle={{ color: TEXT_MUTED, fontSize: "0.7rem" }}
                        />
                      </div>
                      <div className="fw-semibold" style={{ fontSize: "0.92rem", color: COLOR_SUCCESS, letterSpacing: "-0.2px", wordBreak: "break-word" }}>
                        {formatCurrency(
                          Number(center.term_paid_amount || 0) + Number(center.otherfees_paid_amount || 0)
                        )}
                      </div>
                    </div>

                    <div style={{ minWidth: "90px", flex: "1 1 auto" }}>
                      <div style={{ fontSize: "0.7rem", color: TEXT_MUTED }}>Late Fee Paid</div>
                      <div className="fw-semibold" style={{ fontSize: "0.92rem", color: COLOR_SUCCESS, letterSpacing: "-0.2px", wordBreak: "break-word" }}>
                        {formatCurrency(
                          Number(center.term_latefee_paid_amount || 0) +
                          Number(center.otherfees_latefee_paid_amount || 0)
                        )}
                      </div>
                    </div>

                    <div style={{ minWidth: "90px", flex: "1 1 auto" }}>
                      <div style={{ fontSize: "0.7rem", color: TEXT_MUTED }}>
                        <InfoLabel
                          id={`center-overall-paid-${center.center_id}`}
                          label="Overall Paid"
                          tooltip={overallPaidTooltip}
                          labelStyle={{ color: TEXT_MUTED, fontSize: "0.7rem" }}
                        />
                      </div>
                      <div className="fw-bold" style={{ fontSize: "0.92rem", color: COLOR_SUCCESS, letterSpacing: "-0.2px", wordBreak: "break-word" }}>
                        {formatCurrency(collected)}
                      </div>
                    </div>
                  </div>
                </Col>

                {/* Collection Progress & Action Chevron */}
                <Col xs={12} sm={3} md={3} className="text-end d-flex align-items-center justify-content-end gap-4">
                  <div>
                    <div className="fw-bold" style={{ fontSize: "1rem", color: COLOR_SUCCESS, letterSpacing: "-0.3px" }}>
                      {formatCurrency(collected)}
                    </div>
                    <div className="d-flex align-items-center gap-2 mt-0.5 justify-content-end">
                      <Progress
                        value={collectionPercent}
                        color="success"
                        style={{ width: "80px", height: "4px", borderRadius: "2px", backgroundColor: "var(--talc-border)" }}
                      />
                      <span className="fw-semibold" style={{ fontSize: "0.72rem", color: TEXT_MUTED }}>
                        {collectionPercent}%
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: isExpanded ? "var(--talc-bg-subtle)" : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--talc-text-muted)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Collapsible Detailed View */}
            <Collapse isOpen={isExpanded}>
              <div className="p-4" style={{ background: "var(--talc-bg-subtle)", borderTop: "1px solid var(--talc-border)" }}>
                <Row className="g-3">
                  {/* ── Left Column: Detailed Fee Ledgers ────────────────────── */}
                  <Col xs={12} lg={7} className="d-flex flex-column gap-3">
                    {/* 1. Term Fees Card */}
                    {(() => {
                      const termBilled = Number(center.term_total_amount || 0) + Number(center.term_skipped_amount || 0);
                      const termPureRefund = Number(center.term_pure_refund_amount ?? center.term_refund_amount ?? 0);
                      const termHistoricalPaid = Number(center.term_historical_paid_amount || 0);
                      const termHistoricalRefund = Number(center.term_historical_refund_amount || 0);
                      // Previous program dues are represented by what was actually
                      // paid there — added into expected so the payment offsets its
                      // own dues instead of wrongly reducing current-program dues.
                      const termExpected = termBilled + termHistoricalPaid;
                      const prevProgramDiscountUnpaid = Number(center.term_historical_discount_amount || 0);
                      const prevProgramDiscountPaid = Number(center.term_historical_discounted_amount || 0);
                      const prevProgramDiscountTotal = prevProgramDiscountUnpaid + prevProgramDiscountPaid;
                      const concessionTotal =
                        Number(center.term_expected_discount || 0) +
                        Number(center.term_discounted_amount || 0);
                      // Remaining = only this card's values (no late fee). Concession
                      // (expected + used) is subtracted exactly once.
                      const termBal = Math.max(
                        0,
                        termExpected
                          - Number(center.term_skipped_amount || 0)
                          - Number(center.term_disabled_amount || 0)
                          - concessionTotal
                          - termPureRefund
                          - Number(center.term_paid_amount || 0)
                          - termHistoricalPaid
                          + termHistoricalRefund
                      );
                      const isSettled = termBal <= 0;
                      const termBalanceTooltip = (
                        <TooltipRows
                          rows={[
                            { label: "Term Amount Expected", value: formatCurrency(termExpected) },
                            { label: "Skipped Term Amount", value: `-${formatCurrency(center.term_skipped_amount || 0)}` },
                            { label: "Disabled Term Amount", value: `-${formatCurrency(center.term_disabled_amount || 0)}` },
                            { label: "Concession Discount (Total)", value: `-${formatCurrency(concessionTotal)}` },
                            ...(prevProgramDiscountTotal > 0
                              ? [{ label: "Previous Program Discount", value: formatCurrency(prevProgramDiscountTotal) }]
                              : []),
                            ...(prevProgramDiscountPaid > 0
                              ? [{ label: "Previous Program Discount Paid", value: formatCurrency(prevProgramDiscountPaid) }]
                              : []),
                            { label: "Fee Refund", value: `-${formatCurrency(termPureRefund)}` },
                            { label: "Amount Paid", value: `-${formatCurrency(center.term_paid_amount || 0)}` },
                            { label: "Previous Program Paid", value: `-${formatCurrency(termHistoricalPaid)}` },
                            { label: "Previous Program Refund", value: `+${formatCurrency(termHistoricalRefund)}` },
                          ]}
                          total={{ label: "Remaining Balance Due", value: formatCurrency(termBal) }}
                        />
                      );

                      return (
                        <div
                          className="p-3 rounded-3"
                          style={{
                            background: "var(--talc-text-white)",
                            border: "1px solid var(--talc-border)",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2.5 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 26, height: 26, borderRadius: "6px", background: "var(--talc-bg-subtle)", color: "var(--talc-text-secondary)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                <FaBookOpen size={12} />
                              </span>
                              <span className="fw-bold" style={{ fontSize: "0.85rem", color: "var(--talc-text-primary)" }}>
                                Term Fees Breakdown
                              </span>
                            </div>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: "12px",
                                background: isSettled ? "var(--talc-success-bg)" : "var(--talc-danger-bg)",
                                color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)",
                                border: isSettled ? "1px solid var(--talc-success-border)" : "1px solid var(--talc-danger-border)",
                              }}
                            >
                              {isSettled ? "✓ Fully Settled" : "Pending"}
                            </span>
                          </div>

                          <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                            <div className="d-flex flex-wrap justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Term Amount Expected</span>
                              <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(termExpected)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Skipped Term Amount</span>
                              <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(center.term_skipped_amount || 0)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-danger-text)" }}>Disabled Term Amount</span>
                              <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(center.term_disabled_amount || 0)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Concession Discount (Total)</span>
                              <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(concessionTotal)}</span>
                            </div>

                            {Number(center.term_discounted_amount || 0) > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-text-secondary)" }}>Discount Applied (Paid)</span>
                                <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>-{formatCurrency(center.term_discounted_amount || 0)}</span>
                              </div>
                            )}

                            {prevProgramDiscountTotal > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-text-muted)" }}>Previous Program Discount</span>
                                <span className="fw-bold" style={{ color: "var(--talc-text-muted)" }}>{formatCurrency(prevProgramDiscountTotal)}</span>
                              </div>
                            )}

                            {prevProgramDiscountPaid > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-text-muted)" }}>Previous Program Discount Paid</span>
                                <span className="fw-bold" style={{ color: "var(--talc-text-muted)" }}>{formatCurrency(prevProgramDiscountPaid)}</span>
                              </div>
                            )}

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Fee Refund</span>
                              <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(termPureRefund)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-success-text)", fontWeight: 600 }}>Amount Paid</span>
                              <span className="fw-bold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(center.term_paid_amount || 0)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Previous Program Paid</span>
                              <span className="fw-bold" style={{ color: "var(--talc-warning-text)" }}>-{formatCurrency(termHistoricalPaid)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Previous Program Refund</span>
                              <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(termHistoricalRefund)}</span>
                            </div>

                            <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "var(--talc-border)" }}>
                              <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)" }}>
                                <InfoLabel
                                  id={`term-remain-${center.center_id}`}
                                  label="Remaining Balance Due"
                                  tooltip={termBalanceTooltip}
                                  labelStyle={{ color: "inherit", fontWeight: 700 }}
                                />
                              </span>
                              <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)", fontSize: "0.85rem" }}>{formatCurrency(termBal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* 2. Other Fees Card */}
                    {(() => {
                      const otherLateExpected = Number(center.otherfees_expected_latefee_amount || 0);
                      const otherBilled = Math.max(0, Number(center.otherfees_total_amount || 0) - otherLateExpected);
                      const otherPaid = Number(center.otherfees_paid_amount || 0);
                      const otherPureRefund = Number(center.otherfees_pure_refund_amount ?? center.otherfees_refund_amount ?? 0);
                      const otherHistoricalPaid = Number(center.otherfees_historical_paid_amount || 0);
                      const otherHistoricalRefund = Number(center.otherfees_historical_refund_amount || 0);
                      // Remaining = only this card's values (no late fee).
                      const otherBal = Math.max(
                        0,
                        otherBilled
                          - otherPureRefund
                          - otherPaid
                          - otherHistoricalPaid
                          + otherHistoricalRefund
                      );
                      const isSettled = otherBal <= 0;
                      const otherBalanceTooltip = (
                        <TooltipRows
                          rows={[
                            { label: "Other Fee Expected", value: formatCurrency(otherBilled) },
                            { label: "Fee Refund", value: `-${formatCurrency(otherPureRefund)}` },
                            { label: "Amount Paid", value: `-${formatCurrency(otherPaid)}` },
                            { label: "Previous Program Paid", value: `-${formatCurrency(otherHistoricalPaid)}` },
                            { label: "Previous Program Refund", value: `+${formatCurrency(otherHistoricalRefund)}` },
                          ]}
                          total={{ label: "Remaining Balance Due", value: formatCurrency(otherBal) }}
                        />
                      );

                      return (
                        <div
                          className="p-3 rounded-3"
                          style={{
                            background: "var(--talc-text-white)",
                            border: "1px solid var(--talc-border)",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2.5 pb-2 border-bottom">
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 26, height: 26, borderRadius: "6px", background: "var(--talc-bg-subtle)", color: "var(--talc-text-secondary)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                                <FaFileInvoiceDollar size={12} />
                              </span>
                              <div>
                                <span className="fw-bold" style={{ fontSize: "0.85rem", color: "var(--talc-text-primary)" }}>
                                  Other Fees Breakdown
                                </span>
                              </div>
                            </div>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                padding: "3px 8px",
                                borderRadius: "12px",
                                background: isSettled ? "var(--talc-success-bg)" : "var(--talc-danger-bg)",
                                color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)",
                                border: isSettled ? "1px solid var(--talc-success-border)" : "1px solid var(--talc-danger-border)",
                              }}
                            >
                              {isSettled ? "✓ Fully Settled" : "Pending"}
                            </span>
                          </div>

                          <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Other Fee Expected</span>
                              <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(otherBilled)}</span>
                            </div>

                            {/* {otherLateExpected > 0 && (
                              <div className="d-flex justify-content-between py-1 px-1">
                                <span style={{ color: "var(--talc-warning-text)" }}>Other Late Fee</span>
                                <span className="fw-bold" style={{ color: "var(--talc-warning-text)" }}>+{formatCurrency(otherLateExpected)}</span>
                              </div>
                            )} */}

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Fee Refund</span>
                              <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(otherPureRefund)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-success-text)", fontWeight: 600 }}>Amount Paid</span>
                              <span className="fw-bold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(otherPaid)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Previous Program Paid</span>
                              <span className="fw-bold" style={{ color: "var(--talc-warning-text)" }}>+{formatCurrency(otherHistoricalPaid)}</span>
                            </div>

                            <div className="d-flex justify-content-between py-1 px-1">
                              <span style={{ color: "var(--talc-text-secondary)" }}>Previous Program Refund</span>
                              <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>-{formatCurrency(otherHistoricalRefund)}</span>
                            </div>

                            <div className="d-flex justify-content-between pt-2 px-1 mt-1 border-top" style={{ borderColor: "var(--talc-border)" }}>
                              <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)" }}>
                                <InfoLabel
                                  id={`other-remain-${center.center_id}`}
                                  label="Remaining Balance Due"
                                  tooltip={otherBalanceTooltip}
                                  labelStyle={{ color: "inherit", fontWeight: 700 }}
                                />
                              </span>
                              <span className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)", fontSize: "0.85rem" }}>{formatCurrency(otherBal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                  </Col>

                  {/* ── Right Column: Payment Channels & Overall Summary ───── */}
                  <Col xs={12} lg={5} className="d-flex flex-column gap-3">
                    <LateFeeBreakdownCard
                      title="Term Late Fee Breakdown"
                      expected={
                        Number(center.term_expected_latefee_amount || 0) +
                        centerHistoricalLateFeePaid -
                        centerHistoricalLateFeeRefund
                      }
                      paid={centerCurrentLateFeePaid}
                      historicalPaid={centerHistoricalLateFeePaid}
                      historicalRefund={centerHistoricalLateFeeRefund}
                      refunded={centerCurrentLateFeeRefund}
                      tooltipKey={`term-${center.center_id}`}
                      unpaidTooltip={termLateUnpaidTooltip}
                      unpaid={Math.max(
                        0,
                        Number(center.term_expected_latefee_amount || 0) -
                        centerCurrentLateFeePaidGross
                      )}
                    />

                    <LateFeeBreakdownCard
                      title="Other Late Fee Breakdown"
                      expected={center.otherfees_expected_latefee_amount}
                      paid={center.otherfees_latefee_paid_amount}
                      historicalPaid={centerHistoricalOtherLateFeePaid}
                      historicalRefund={centerHistoricalOtherLateFeeRefund}
                      refunded={centerCurrentOtherLateFeeRefund}
                      tooltipKey={`other-${center.center_id}`}
                      unpaidTooltip={otherLateUnpaidTooltip}
                      unpaid={centerOtherLateFeeUnpaid}
                    />

                    {/* Observation & Registration Direct Fees */}
                    <div
                      className="p-3 rounded-3"
                      style={{
                        background: "var(--talc-text-white)",
                        border: "1px solid var(--talc-border)",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                      }}
                    >
                      <div className="fw-bold mb-2 pb-1 border-bottom d-flex align-items-center" style={{ fontSize: "0.82rem", color: "var(--talc-text-primary)" }}>
                        <FaStethoscope size={13} className="text-muted me-2" />
                        Observation & Registration Direct Fees
                      </div>
                      <div className="d-flex flex-column gap-1" style={{ fontSize: "0.78rem" }}>
                        <div className="d-flex justify-content-between py-1 px-1">
                          <span style={{ color: "var(--talc-text-secondary)" }}>Observation Fees Paid</span>
                          <span className="fw-bold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(center.observation_paid_amount || 0)}</span>
                        </div>
                        <div className="d-flex justify-content-between py-1 px-1">
                          <span style={{ color: "var(--talc-text-secondary)" }}>Registration Fees Paid</span>
                          <span className="fw-bold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(center.registeration_paid_amount || 0)}</span>
                        </div>
                      </div>
                    </div>

                  </Col>

                  {/* ── Payment Channels (Full Width) ─────────────────────────── */}
                  {(() => {
                    const channelEntries = Object.entries(center.payment_channels || {})
                      .sort(([, a], [, b]) => (Number(b?.received ?? b) || 0) - (Number(a?.received ?? a) || 0));
                    return (
                      <Col xs={12}>
                        <div
                          className="p-3 rounded-3"
                          style={{
                            background: "var(--talc-text-white)",
                            border: "1px solid var(--talc-border)",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2.5 border-bottom pb-2">
                            <span className="fw-bold d-flex align-items-center" style={{ fontSize: "0.82rem", color: "var(--talc-text-primary)" }}>
                              <FaCreditCard size={13} className="text-muted me-2" />
                              Payment Channels
                            </span>
                            <span style={{ fontSize: "0.68rem", color: "var(--talc-text-muted)" }}>
                              Gross Received: <strong style={{ color: TEXT_DARK }}>{formatCurrency(totalChannelReceived)}</strong>
                            </span>
                          </div>

                          {channelEntries.length === 0 ? (
                            <div className="text-center py-3 text-muted" style={{ fontSize: "0.76rem" }}>
                              No payment channel records available.
                            </div>
                          ) : (
                            <>
                              <Row className="g-2">
                                {channelEntries.map(([channel, chData], i) => {
                                  const style = getChannelStyle(channel, i);
                                  const received = Number(chData?.received ?? chData) || 0;
                                  const refunded = Number(chData?.refunded ?? 0) || 0;
                                  const net = Number(chData?.net ?? received - refunded) || 0;
                                  return (
                                    <Col xs={12} sm={6} xl={3} key={i}>
                                      <div
                                        className="p-2 rounded-3 h-100"
                                        style={{
                                          background: style.bg,
                                          border: `1px solid ${style.border}`,
                                        }}
                                      >
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                            <div
                                              style={{
                                                width: 26,
                                                height: 26,
                                                borderRadius: "6px",
                                                background: style.iconBg,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                              }}
                                            >
                                              {style.icon}
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                              <div className="fw-bold text-uppercase" style={{ fontSize: "0.72rem", color: "var(--talc-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {channel}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="d-flex flex-column ps-4" style={{ fontSize: "0.7rem", gap: "2px" }}>
                                          <div className="d-flex justify-content-between">
                                            <span style={{ color: "var(--talc-text-secondary)" }}>Received</span>
                                            <span className="fw-semibold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(received)}</span>
                                          </div>
                                          <div className="d-flex justify-content-between">
                                            <span style={{ color: "var(--talc-text-secondary)" }}>Refunded</span>
                                            <span className="fw-semibold" style={{ color: refunded > 0 ? "var(--talc-danger-text)" : "var(--talc-text-muted)" }}>{formatCurrency(refunded)}</span>
                                          </div>
                                          <div className="d-flex justify-content-between pt-1" style={{ borderTop: `1px dashed ${style.border}` }}>
                                            <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>Net</span>
                                            <span className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(net)}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  );
                                })}
                              </Row>
                              <div className="d-flex justify-content-between pt-2 mt-1 border-top" style={{ fontSize: "0.7rem" }}>
                                <span style={{ color: "var(--talc-text-secondary)" }}>
                                  Total Refunded — <strong style={{ color: "var(--talc-danger-text)" }}>{formatCurrency(totalChannelRefund)}</strong>
                                </span>
                                <span style={{ color: "var(--talc-text-secondary)" }}>
                                  Net Collection — <strong style={{ color: TEXT_DARK }}>{formatCurrency(totalChannel)}</strong>
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </Col>
                    );
                  })()}

                </Row>
              </div>
            </Collapse>
          </Card>
        );
      })}

      {/* View All Centers Button */}
      {centers.length > 5 && (
        <div className="text-center mt-3 mb-2">
          <Button
            color="light"
            className="px-4 py-2 fw-semibold"
            style={{
              borderRadius: "20px",
              border: "1px solid var(--talc-border)",
              color: "var(--talc-text-secondary)",
              fontSize: "0.84rem",
              background: "var(--talc-text-white)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            }}
            onClick={() => setShowAllCenters((prev) => !prev)}
          >
            {showAllCenters ? "Show Fewer Centers" : `View All Centers (${centers.length})`}
          </Button>
        </div>
      )}
    </Container>
  );
};

// ─── Component: FeeBreakdownCard (Complete Executive Redesign) ────────────────
const FeeBreakdownCard = ({
  title,
  total,
  paid,
  historicalPaid = 0,
  refund = 0,
  discount = 0,
  skipped = 0,
  lateTotal = 0,
  latePaid = 0,
  lateRefund = 0,
  balance,
  themeColor = "var(--talc-success-text)",
  icon,
}) => {
  const displayLatePaid = Math.max(0, Number(latePaid));
  const lateUnpaid = Math.max(0, Number(lateTotal) - displayLatePaid - Number(lateRefund));
  const computedBalance = Math.max(
    0,
    Number(total) - Number(paid) - Number(refund) - displayLatePaid - Number(discount)
  );
  const displayBalance = balance !== undefined ? Math.max(0, Number(balance)) : computedBalance;
  const pctPaid = calculatePercentage(paid, total);
  const isSettled = displayBalance <= 0;

  return (
    <Card
      className="talc-card h-100 mb-0"
      style={{
        borderRadius: "var(--talc-radius-lg)",
        border: "1px solid var(--talc-border)",
        background: "var(--talc-bg-card)",
        overflow: "hidden",
      }}
    >
      {/* Card Header */}
      <div
        className="px-3.5 py-2.5 d-flex justify-content-between align-items-center"
        style={{
          background: "var(--talc-bg-subtle)",
          borderBottom: "1px solid var(--talc-border)",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          {icon}
          <span className="fw-bold" style={{ fontSize: "0.86rem", color: TEXT_DARK }}>
            {title}
          </span>
        </div>
        <span
          style={{
            backgroundColor: isSettled ? "var(--talc-success-bg)" : "var(--talc-info-bg)",
            color: isSettled ? "var(--talc-success-text)" : "var(--talc-info-text)",
            fontSize: "0.72rem",
            fontWeight: 700,
            border: isSettled ? "1px solid var(--talc-success-border)" : "1px solid var(--talc-info-border)",
            padding: "3px 10px",
            borderRadius: "20px",
          }}
        >
          {pctPaid}% Paid
        </span>
      </div>

      <CardBody className="p-3.5 d-flex flex-column justify-content-between">
        {/* Progress Bar */}
        <div className="mb-3">
          <div className="d-flex justify-content-between mb-1" style={{ fontSize: "0.7rem", color: "var(--talc-text-muted)" }}>
            <span>Payment Progress</span>
            <span className="fw-semibold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-info-text)" }}>{pctPaid}%</span>
          </div>
          <Progress
            value={pctPaid}
            color={isSettled ? "success" : "primary"}
            style={{ height: "5px", borderRadius: "3px", backgroundColor: "var(--talc-bg-subtle)" }}
          />
        </div>

        {/* 3-Stat Summary Pill Strip */}
        <div
          className="d-flex align-items-center justify-content-between p-2 mb-3 rounded"
          style={{
            background: "var(--talc-bg-subtle)",
            border: "1px solid var(--talc-border)",
            fontSize: "0.76rem",
          }}
        >
          <div className="text-center flex-fill">
            <div style={{ fontSize: "0.66rem", color: "var(--talc-text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Billed</div>
            <div className="fw-bold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(total)}</div>
          </div>
          <div className="border-start text-center flex-fill ps-2">
            <div style={{ fontSize: "0.66rem", color: "var(--talc-success-text)", textTransform: "uppercase", fontWeight: 600 }}>Paid</div>
            <div className="fw-bold" style={{ color: "var(--talc-success-text)" }}>{formatCurrency(paid)}</div>
          </div>
          <div className="border-start text-center flex-fill ps-2">
            <div style={{ fontSize: "0.66rem", color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)", textTransform: "uppercase", fontWeight: 600 }}>Balance</div>
            <div className="fw-bold" style={{ color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)" }}>{formatCurrency(displayBalance)}</div>
          </div>
        </div>

        {/* Line Items List */}
        <div className="d-flex flex-column gap-1 mb-3" style={{ fontSize: "0.76rem" }}>
          {Number(historicalPaid) > 0 && (
            <div className="d-flex align-items-center justify-content-between py-1 px-2 rounded" style={{ background: "var(--talc-bg-subtle)" }}>
              <div className="d-flex align-items-center gap-1.5">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--talc-text-muted)" }}></span>
                <span style={{ color: "var(--talc-text-secondary)" }}>Previous Program Paid</span>
              </div>
              <span className="fw-semibold" style={{ color: "var(--talc-text-muted)" }}>{formatCurrency(historicalPaid)}</span>
            </div>
          )}

          {Number(skipped) > 0 && (
            <div className="d-flex align-items-center justify-content-between py-1 px-2 rounded" style={{ background: "var(--talc-bg-subtle)" }}>
              <div className="d-flex align-items-center gap-1.5">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--talc-text-muted)" }}></span>
                <span style={{ color: "var(--talc-text-secondary)" }}>Skipped Term Amount (Included)</span>
              </div>
              <span className="fw-semibold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(skipped)}</span>
            </div>
          )}

          {Number(discount) > 0 && (
            <div className="d-flex align-items-center justify-content-between py-1 px-2 rounded" style={{ background: "var(--talc-danger-bg)" }}>
              <div className="d-flex align-items-center gap-1.5">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--talc-danger-text)" }}></span>
                <span style={{ color: "var(--talc-text-secondary)" }}>Discount Deduction</span>
              </div>
              <span className="fw-semibold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(discount)}</span>
            </div>
          )}

          {Number(refund) > 0 && (
            <div className="d-flex align-items-center justify-content-between py-1 px-2 rounded" style={{ background: "var(--talc-danger-bg)" }}>
              <div className="d-flex align-items-center gap-1.5">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--talc-danger-text)" }}></span>
                <span style={{ color: "var(--talc-text-secondary)" }}>Refund Amount</span>
              </div>
              <span className="fw-semibold" style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(refund)}</span>
            </div>
          )}
        </div>

        {/* Late Fee Nested Container */}
        {Number(lateTotal) > 0 && (
          <div
            className="p-2.5 rounded mb-3"
            style={{
              background: "var(--talc-danger-bg)",
              border: "1px solid var(--talc-danger-border)",
              fontSize: "0.74rem",
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-1.5">
              <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>
                Late Fee Summary
              </span>
              <span className="fw-bold" style={{ color: "var(--talc-danger-text)" }}>
                Total: {formatCurrency(lateTotal)}
              </span>
            </div>
            <div className="d-flex align-items-center justify-content-between pt-1 border-top" style={{ borderColor: "var(--talc-danger-border)" }}>
              <div className="d-flex align-items-center gap-1">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--talc-success-text)" }}></span>
                <span style={{ color: "var(--talc-text-secondary)" }}>Paid:</span>
                <strong style={{ color: "var(--talc-success-text)" }}>{formatCurrency(displayLatePaid)}</strong>
              </div>
              <div className="d-flex align-items-center gap-1">
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: lateUnpaid > 0 ? "var(--talc-danger-text)" : "var(--talc-text-muted)" }}></span>
                <span style={{ color: "var(--talc-text-secondary)" }}>Unpaid:</span>
                <strong style={{ color: lateUnpaid > 0 ? "var(--talc-danger-text)" : "var(--talc-text-muted)" }}>{formatCurrency(lateUnpaid)}</strong>
              </div>
              {Number(lateRefund) > 0 && (
                <div className="d-flex align-items-center gap-1">
                  <span style={{ color: "var(--talc-text-secondary)" }}>Refund:</span>
                  <strong style={{ color: "var(--talc-text-primary)" }}>{formatCurrency(lateRefund)}</strong>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Final Balance Footer Row */}
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2 rounded"
          style={{
            background: isSettled ? "var(--talc-success-bg)" : "var(--talc-danger-bg)",
            border: isSettled ? "1px solid var(--talc-success-border)" : "1px solid var(--talc-danger-border)",
          }}
        >
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              textTransform: "uppercase",
              color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)",
              letterSpacing: "0.4px",
            }}
          >
            {isSettled ? "✓ Fully Settled" : "Remaining Balance"}
          </span>
          <span
            style={{
              fontSize: "0.92rem",
              fontWeight: 800,
              color: isSettled ? "var(--talc-success-text)" : "var(--talc-danger-text)",
              fontFamily: "monospace, sans-serif",
            }}
          >
            {formatCurrency(displayBalance)}
          </span>
        </div>
      </CardBody>
    </Card>
  );
};

// ─── Component: InfoCard (Redesigned) ────────────────────────────────────────
const InfoCard = ({ title, amount, icon }) => (
  <div
    style={{
      background: "var(--talc-text-white)",
      border: "1px solid var(--talc-border)",
      borderRadius: "12px",
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
    }}
  >
    <div className="d-flex align-items-center gap-2.5">
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          background: "var(--talc-bg-subtle)",
          color: "var(--talc-text-muted)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 700,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--talc-text-secondary)" }}>
          {title}
        </div>
        <div style={{ fontSize: "0.68rem", color: "var(--talc-text-muted)" }}>
          One-time direct fee
        </div>
      </div>
    </div>
    <div className="text-end">
      <div className="fw-bold" style={{ fontSize: "0.92rem", color: "var(--talc-success-text)", fontFamily: "monospace, sans-serif" }}>
        {formatCurrency(amount)}
      </div>
      <span
        style={{
          fontSize: "0.62rem",
          fontWeight: 700,
          padding: "1px 6px",
          borderRadius: "4px",
          background: Number(amount) > 0 ? "var(--talc-success-bg)" : "var(--talc-bg-subtle)",
          color: Number(amount) > 0 ? "var(--talc-success-text)" : "var(--talc-text-muted)",
        }}
      >
        {Number(amount) > 0 ? "Paid" : "None"}
      </span>
    </div>
  </div>
);

export default AcademicFeesDashboard;
